import React from 'react';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { trackContact } from '../lib/pixel';

/** Official WhatsApp logo SVG (white phone-in-bubble on brand-green bg). */
function WhatsAppLogo({ className = '', bg = false }) {
  if (bg) {
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#25D366" />
        <path
          d="M16.78 13.78c-.26-.13-1.55-.76-1.79-.85-.24-.09-.41-.13-.59.13-.17.26-.67.85-.82 1.02-.15.17-.3.2-.56.07-.26-.13-1.1-.4-2.1-1.29-.78-.7-1.3-1.55-1.45-1.82-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.46.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.46-.06-.13-.59-1.42-.8-1.95-.21-.51-.43-.45-.59-.45-.15 0-.32-.02-.5-.02-.17 0-.46.06-.7.32-.24.26-.92.9-.92 2.2 0 1.3.94 2.55 1.07 2.73.13.17 1.85 2.83 4.49 3.97 1.57.68 2.18.73 2.97.61.48-.07 1.55-.63 1.77-1.24.21-.62.21-1.15.15-1.24-.06-.1-.24-.17-.5-.3Z"
          fill="#fff"
        />
        <path
          d="M11.97 5.6c-3.51 0-6.36 2.85-6.36 6.36 0 1.12.29 2.21.85 3.17l-.91 3.33 3.41-.89c.93.51 1.97.78 3.01.78 3.51 0 6.36-2.85 6.36-6.36s-2.85-6.36-6.36-6.39Zm0 1.45c2.74 0 4.94 2.2 4.94 4.94s-2.2 4.94-4.94 4.94c-.93 0-1.83-.26-2.61-.74l-.18-.11-1.94.51.52-1.89-.12-.19a4.94 4.94 0 0 1-.76-2.62c0-2.72 2.2-4.94 4.94-4.94Z"
          fill="#fff"
        />
      </svg>
    );
  }
  // White-only icon (parent button provides the green background)
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path
        d="M19.05 4.94A9.94 9.94 0 0 0 12 2.02a9.96 9.96 0 0 0-8.6 14.97L2 22l5.13-1.34a9.96 9.96 0 0 0 4.86 1.24h.01c5.5 0 9.96-4.45 9.96-9.96 0-2.66-1.04-5.16-2.91-7.0Zm-7.05 15.3c-1.5 0-2.96-.4-4.24-1.16l-.3-.18-3.05.8.81-2.97-.2-.31a8.27 8.27 0 0 1-1.26-4.4c0-4.57 3.72-8.29 8.29-8.29 2.21 0 4.29.86 5.86 2.43a8.23 8.23 0 0 1 2.43 5.86c0 4.57-3.72 8.29-8.29 8.29Zm4.55-6.21c-.25-.13-1.47-.73-1.7-.81-.23-.08-.39-.13-.56.13-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.38-.43.13-.14.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.36-.77-1.86-.2-.49-.41-.42-.56-.43-.14-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07s.89 2.4 1.02 2.57c.13.17 1.76 2.7 4.27 3.78 1.49.65 2.07.7 2.81.59.45-.07 1.39-.57 1.59-1.12.2-.55.2-1.02.14-1.12-.06-.1-.23-.17-.48-.3Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Reusable WhatsApp CTA — opens wa.me link with optional prefilled message.
 *
 * Default destination is Claudia · Customer Care (+39 351 980 1113).
 * Override with `phone` prop for other contacts (e.g. specific agent).
 */
export const WHATSAPP_DEFAULT_NUMBER = '393519801113'; // Claudia · HCF Customer Care
export const WHATSAPP_DEFAULT_TEXT = "Ciao, sono interessato a Green Paradise (HCF Group). Mi spiegate come funziona?";

/**
 * Fire a fire-and-forget tracking call to /api/public/whatsapp-handoff.
 *
 * The backend takes care of:
 *   - looking up the lead (by id, email or phone)
 *   - reassigning it to Claudia (Community Manager)
 *   - stopping the state-machine DMs to the previous Associate
 *   - notifying the previous Associate of the revoke
 *   - notifying Claudia of the inbound
 *
 * Uses `keepalive: true` so the request survives the cross-origin nav to
 * `wa.me/...`. Failures are silent — we MUST never block the user click.
 */
function trackWhatsappHandoff({ source, leadId, leadEmail, leadPhone }) {
  try {
    const payload = {
      lead_id: leadId || null,
      lead_email: leadEmail || null,
      lead_phone: leadPhone || null,
      source: source || 'unknown',
      page_context:
        typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : null,
    };
    // Prefer api.post for normal flows so baseURL + interceptors apply.
    // We don't await: the redirect to wa.me happens immediately after.
    api.post('/public/whatsapp-handoff', payload).catch((err) => {
      logger.debug('whatsapp-handoff tracking failed', err?.message);
    });
  } catch (err) {
    logger.debug('whatsapp-handoff tracking threw', err?.message);
  }
}

export default function WhatsAppButton({
  variant = 'solid',
  label = 'Scrivici su WhatsApp',
  size = 'md',
  className = '',
  source = 'cta',
  phone = WHATSAPP_DEFAULT_NUMBER,
  text = WHATSAPP_DEFAULT_TEXT,
  // Optional lead identifiers — when present the backend will hand off
  // the lead to Claudia and revoke the previous Associate.
  leadId,
  leadEmail,
  leadPhone,
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-semibold tracking-[0.04em] transition-all duration-150 whitespace-nowrap';
  const sizes = {
    sm: 'h-9 px-3 text-[12px] rounded-full',
    md: 'h-11 px-5 text-sm rounded-full',
    lg: 'h-12 md:h-14 px-5 md:px-7 text-sm md:text-base rounded-full',
  };
  const variants = {
    solid:
      'bg-[#25D366] text-white hover:bg-[#1DAE52] shadow-lg hover:shadow-xl',
    outline:
      'bg-white text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/10 hover:border-[#25D366]',
    ghost:
      'bg-transparent text-[#25D366] hover:underline underline-offset-4',
    pill:
      'bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 h-8 px-3 text-[11px] rounded-full',
  };
  const finalSize = variant === 'pill' ? '' : sizes[size] || sizes.md;
  const iconSize =
    size === 'lg' ? 'w-5 h-5 md:w-6 md:h-6' : size === 'sm' ? 'w-4 h-4' : 'w-4 h-4 md:w-5 md:h-5';
  const logoBg = variant !== 'solid';
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;

  const handleClick = () => {
    // Fire-and-forget handoff tracking. We don't await — the browser will
    // navigate to wa.me immediately; the request is `keepalive` inside api.
    trackWhatsappHandoff({ source, leadId, leadEmail, leadPhone });
    // ★ Meta Pixel — Contact event
    try { trackContact({ channel: 'whatsapp', source }); } catch (err) {
      logger.debug('WhatsAppButton: pixel trackContact failed', err);
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`${base} ${finalSize} ${variants[variant]} ${className}`}
      data-testid={`whatsapp-cta-${source}`}
    >
      <WhatsAppLogo className={iconSize} bg={logoBg} />
      <span>{label}</span>
    </a>
  );
}
